import { useEffect, useState } from "react";
import { CalendarPlus, Save, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

function arrayToText(value) {
  return Array.isArray(value) ? value.join(", ") : "";
}

function splitText(value) {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export const ClientOperationsEditor = ({ client, onClose, onSaved }) => {
  const [profile, setProfile] = useState(null);
  const [budget, setBudget] = useState({ fiscal_year: 2026, month: "", amount: "" });
  const [utilization, setUtilization] = useState({
    service_type: "",
    service_month: "",
    contracted_hours: "",
    scheduled_hours: "",
    completed_hours: "",
    missed_hours: "",
  });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!client) {
      return;
    }
    setProfile({
      name: client.name || "",
      region: client.region || "",
      state: client.state || "",
      district: client.district || "",
      account_manager: client.account_manager || "",
      profile: client.profile || "",
      annual_sped_budget: client.annual_sped_budget || 0,
      service_lines: arrayToText(client.service_lines),
      primary_contacts: arrayToText(client.primary_contacts),
      contract_start_date: client.contract_start_date || "",
      contract_end_date: client.contract_end_date || "",
      renewal_date: client.renewal_date || "",
      iep_heavy_periods: arrayToText(client.iep_heavy_periods),
    });
    setError("");
  }, [client]);

  if (!client || !profile) {
    return null;
  }

  async function saveProfile(event) {
    event.preventDefault();
    try {
      const response = await api.put(`/data-administration/clients/${client.id}`, {
        ...profile,
        annual_sped_budget: Number(profile.annual_sped_budget),
        service_lines: splitText(profile.service_lines),
        primary_contacts: splitText(profile.primary_contacts),
        iep_heavy_periods: splitText(profile.iep_heavy_periods),
      });
      onSaved(response.data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function saveBudget(event) {
    event.preventDefault();
    try {
      await api.put("/data-administration/budgets", {
        client_id: client.id,
        fiscal_year: Number(budget.fiscal_year),
        month: budget.month ? Number(budget.month) : null,
        amount: Number(budget.amount),
      });
      setBudget({ ...budget, amount: "" });
      onSaved();
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  async function saveUtilization(event) {
    event.preventDefault();
    try {
      await api.post("/data-administration/utilization", {
        client_id: client.id,
        provider_id: "",
        service_type: utilization.service_type,
        service_month: utilization.service_month,
        contracted_hours: Number(utilization.contracted_hours || 0),
        scheduled_hours: Number(utilization.scheduled_hours || 0),
        completed_hours: Number(utilization.completed_hours || 0),
        missed_hours: Number(utilization.missed_hours || 0),
        provider_cancelled_hours: 0,
        school_cancelled_hours: 0,
        makeup_hours: 0,
      });
      setUtilization({
        service_type: "",
        service_month: "",
        contracted_hours: "",
        scheduled_hours: "",
        completed_hours: "",
        missed_hours: "",
      });
      onSaved();
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  return (
    <section className="client-operations-editor" data-testid="client-operations-editor">
      <div className="section-heading">
        <div>
          <h2>Edit client operations</h2>
          <p>Update district records where the work happens.</p>
        </div>
        <button
          aria-label="Close client editor"
          className="projection-icon-button"
          data-testid="close-client-operations-editor"
          type="button"
          onClick={onClose}
        >
          <X size={16} />
        </button>
      </div>
      {error && (
        <p className="form-message error-message" data-testid="client-operations-editor-error">
          {error}
        </p>
      )}
      <form
        className="client-operations-profile-form"
        data-testid="operations-client-profile-form"
        onSubmit={saveProfile}
      >
        <label>
          Client / district name
          <input
            data-testid="operations-client-name-input"
            value={profile.name}
            onChange={(event) => setProfile({ ...profile, name: event.target.value })}
          />
        </label>
        <label>
          State
          <input
            data-testid="operations-client-state-input"
            value={profile.state}
            onChange={(event) => setProfile({ ...profile, state: event.target.value })}
          />
        </label>
        <label>
          District
          <input
            data-testid="operations-client-district-input"
            value={profile.district}
            onChange={(event) => setProfile({ ...profile, district: event.target.value })}
          />
        </label>
        <label>
          Account manager
          <input
            data-testid="operations-client-manager-input"
            value={profile.account_manager}
            onChange={(event) => setProfile({ ...profile, account_manager: event.target.value })}
          />
        </label>
        <label>
          Annual SPED budget
          <input
            data-testid="operations-client-annual-budget-input"
            min="0"
            step="0.01"
            type="number"
            value={profile.annual_sped_budget}
            onChange={(event) => setProfile({ ...profile, annual_sped_budget: event.target.value })}
          />
        </label>
        <label>
          Service lines
          <input
            data-testid="operations-client-services-input"
            value={profile.service_lines}
            onChange={(event) => setProfile({ ...profile, service_lines: event.target.value })}
          />
        </label>
        <label>
          Contract start
          <input
            data-testid="operations-client-contract-start-input"
            type="date"
            value={profile.contract_start_date}
            onChange={(event) =>
              setProfile({ ...profile, contract_start_date: event.target.value })
            }
          />
        </label>
        <label>
          Contract end
          <input
            data-testid="operations-client-contract-end-input"
            type="date"
            value={profile.contract_end_date}
            onChange={(event) => setProfile({ ...profile, contract_end_date: event.target.value })}
          />
        </label>
        <label>
          Renewal date
          <input
            data-testid="operations-client-renewal-input"
            type="date"
            value={profile.renewal_date}
            onChange={(event) => setProfile({ ...profile, renewal_date: event.target.value })}
          />
        </label>
        <label>
          IEP-heavy periods
          <input
            data-testid="operations-client-iep-periods-input"
            value={profile.iep_heavy_periods}
            onChange={(event) => setProfile({ ...profile, iep_heavy_periods: event.target.value })}
          />
        </label>
        <label className="client-editor-full">
          Primary contacts
          <input
            data-testid="operations-client-contacts-input"
            value={profile.primary_contacts}
            onChange={(event) => setProfile({ ...profile, primary_contacts: event.target.value })}
          />
        </label>
        <label className="client-editor-full">
          Internal profile
          <textarea
            data-testid="operations-client-profile-input"
            rows="3"
            value={profile.profile}
            onChange={(event) => setProfile({ ...profile, profile: event.target.value })}
          />
        </label>
        <button
          className="button button-primary"
          data-testid="save-operations-client-profile-button"
        >
          <Save size={16} /> Save client profile
        </button>
      </form>
      <form
        className="client-operations-budget-form"
        data-testid="operations-sped-budget-form"
        onSubmit={saveBudget}
      >
        <h3>Annual or monthly SPED budget</h3>
        <label>
          Fiscal year
          <input
            data-testid="operations-sped-budget-year-input"
            min="2000"
            type="number"
            value={budget.fiscal_year}
            onChange={(event) => setBudget({ ...budget, fiscal_year: event.target.value })}
          />
        </label>
        <label>
          Month
          <select
            data-testid="operations-sped-budget-month-select"
            value={budget.month}
            onChange={(event) => setBudget({ ...budget, month: event.target.value })}
          >
            <option value="">Annual budget</option>
            {Array.from({ length: 12 }, (_, index) => (
              <option key={index + 1} value={index + 1}>
                {index + 1}
              </option>
            ))}
          </select>
        </label>
        <label>
          Amount
          <input
            data-testid="operations-sped-budget-amount-input"
            min="0"
            required
            step="0.01"
            type="number"
            value={budget.amount}
            onChange={(event) => setBudget({ ...budget, amount: event.target.value })}
          />
        </label>
        <button
          className="button button-secondary"
          data-testid="save-operations-sped-budget-button"
        >
          <CalendarPlus size={16} /> Save budget
        </button>
      </form>
      <form
        className="client-operations-budget-form"
        data-testid="operations-utilization-form"
        onSubmit={saveUtilization}
      >
        <h3>Service utilization</h3>
        <label>
          Service type
          <input
            data-testid="operations-utilization-service-input"
            required
            value={utilization.service_type}
            onChange={(event) =>
              setUtilization({ ...utilization, service_type: event.target.value })
            }
          />
        </label>
        <label>
          Service month
          <input
            data-testid="operations-utilization-month-input"
            required
            type="month"
            value={utilization.service_month}
            onChange={(event) =>
              setUtilization({ ...utilization, service_month: event.target.value })
            }
          />
        </label>
        <label>
          Contracted hours
          <input
            data-testid="operations-utilization-contracted-input"
            min="0"
            step="0.25"
            type="number"
            value={utilization.contracted_hours}
            onChange={(event) =>
              setUtilization({ ...utilization, contracted_hours: event.target.value })
            }
          />
        </label>
        <label>
          Scheduled hours
          <input
            data-testid="operations-utilization-scheduled-input"
            min="0"
            step="0.25"
            type="number"
            value={utilization.scheduled_hours}
            onChange={(event) =>
              setUtilization({ ...utilization, scheduled_hours: event.target.value })
            }
          />
        </label>
        <label>
          Completed hours
          <input
            data-testid="operations-utilization-completed-input"
            min="0"
            step="0.25"
            type="number"
            value={utilization.completed_hours}
            onChange={(event) =>
              setUtilization({ ...utilization, completed_hours: event.target.value })
            }
          />
        </label>
        <label>
          Missed hours
          <input
            data-testid="operations-utilization-missed-input"
            min="0"
            step="0.25"
            type="number"
            value={utilization.missed_hours}
            onChange={(event) =>
              setUtilization({ ...utilization, missed_hours: event.target.value })
            }
          />
        </label>
        <button
          className="button button-secondary"
          data-testid="save-operations-utilization-button"
        >
          <CalendarPlus size={16} /> Save utilization
        </button>
      </form>
    </section>
  );
};
